import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { AppLocale } from '@/i18n/config';
import { formatAmountMinor, formatCompetenceMonth } from '../finance-format';
import type { FinanceDashboard } from './dashboard-model';

type MonthlySeries = FinanceDashboard['monthlySeries'];

type MonthlySeriesCardProps = {
  monthlySeries: MonthlySeries;
  selectedYear: number;
  hasActiveFilter: boolean;
  locale: AppLocale;
};

function pointPosition(
  point: MonthlySeries[number],
  index: number,
  count: number,
  maxMagnitude: number,
) {
  const x = 60 + (index * 460) / Math.max(count - 1, 1);
  const y = 100 - (point.netMinor / maxMagnitude) * 70;
  return { x, y };
}

function SeriesChart({
  monthlySeries,
  selectedYear,
  locale,
}: Omit<MonthlySeriesCardProps, 'hasActiveFilter'>) {
  const { t } = useTranslation('finance');
  const maxMagnitude = Math.max(...monthlySeries.map((point) => Math.abs(point.netMinor)), 1);
  const plotted = monthlySeries.map((point, index) => ({
    point,
    ...pointPosition(point, index, monthlySeries.length, maxMagnitude),
  }));

  return (
    <svg
      aria-label={t('dashboard.monthlySeriesAria', { year: selectedYear })}
      viewBox="0 0 600 220"
      className="border-border bg-card h-[220px] w-full rounded-lg border"
    >
      <line x1="40" y1="180" x2="560" y2="180" stroke="currentColor" opacity="0.2" />
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        points={plotted.map(({ x, y }) => `${x},${y}`).join(' ')}
      />
      {plotted.map(({ point, x, y }) => (
        <g key={point.competenceMonth}>
          <circle cx={x} cy={y} r="5" fill="currentColor" />
          <text x={x} y="202" textAnchor="middle" fontSize="12">
            {formatCompetenceMonth(point.competenceMonth, locale)}
          </text>
        </g>
      ))}
    </svg>
  );
}

function SeriesTable({
  monthlySeries,
  locale,
}: {
  monthlySeries: MonthlySeries;
  locale: AppLocale;
}) {
  const { t } = useTranslation('finance');

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <th className="py-2 pr-3">{t('dashboard.monthColumn')}</th>
            <th className="py-2">{t('dashboard.netColumn')}</th>
          </tr>
        </thead>
        <tbody>
          {monthlySeries.map((point) => (
            <tr key={point.competenceMonth} className="border-border border-t">
              <td className="py-2 pr-3">{formatCompetenceMonth(point.competenceMonth, locale)}</td>
              <td className="py-2">{formatAmountMinor(point.netMinor, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MonthlySeriesCard({
  monthlySeries,
  selectedYear,
  hasActiveFilter,
  locale,
}: MonthlySeriesCardProps) {
  const { t } = useTranslation('finance');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.monthlySeriesTitle', { year: selectedYear })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {monthlySeries.length === 0 ? (
          // The second of the two sites. An empty series under an active filter is an empty
          // SELECTION; saying "no movements available for the selected year" would be false, and
          // it is the same discrimination the register table makes below.
          <p className="text-sm text-foreground/60">
            {hasActiveFilter ? t('dashboard.filteredEmpty') : t('dashboard.empty')}
          </p>
        ) : (
          <>
            <SeriesChart
              monthlySeries={monthlySeries}
              selectedYear={selectedYear}
              locale={locale}
            />
            <SeriesTable monthlySeries={monthlySeries} locale={locale} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
